import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Heart,
  Sparkles,
  GraduationCap,
  Play,
  ArrowRight,
  Check,
} from 'lucide-react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';

const redGradient =
  'linear-gradient(135deg,rgba(255,255,255,.15),transparent 50%,rgba(0,0,0,.15)), #b30000';

const Eyebrow: React.FC<{ children: React.ReactNode; light?: boolean }> = ({ children, light }) => (
  <p className={`text-xs font-semibold tracking-[0.12em] uppercase mb-3 ${light ? 'text-white/60' : 'text-[#970000]'}`}>
    {children}
  </p>
);

const quranFaqs = [
  {
    question: 'Wie lange dauert es, den Quran auf Arabisch zu verstehen?',
    answer:
      'Mit strukturiertem Unterricht liest du nach 3–5 Monaten das arabische Alphabet, nach 1–1,5 Jahren verstehst du grundlegende Grammatik und einfache Texte, und nach 2–3 Jahren liest du Quran-Texte mit echtem Verständnis. Entscheidend ist ein systematischer Lernweg statt isoliertem Vokabellernen.',
  },
  {
    question: 'Muss ich erst Hocharabisch lernen, um den Quran zu verstehen?',
    answer:
      'Ja – der Quran ist in klassischem Hocharabisch (Fuṣḥā) offenbart. Wer Hocharabisch systematisch lernt, versteht damit auch die Sprache des Qurans. Dialekte helfen dafür nicht weiter. Deshalb unterrichten wir ausschließlich Hocharabisch, von der ersten Stunde an mit Blick auf das Quran-Verständnis.',
  },
  {
    question: 'Reicht es, Tajweed zu lernen, um den Quran zu verstehen?',
    answer:
      'Tajweed lehrt die korrekte Rezitation – also die Aussprache. Für das Verständnis brauchst du zusätzlich Grammatik (Nahw), Morphologie (Sarf) und Wortschatz. In unseren Kursen lernst du beides: Im Buchstabenkurs grundlegende Tajweed-Regeln, ab dem Grundlagenkurs die Grammatik, die dir die Bedeutung erschließt.',
  },
  {
    question: 'Kann ich Quran-Arabisch online lernen?',
    answer:
      'Ja. Unsere Live-Kurse finden vollständig online statt – auf Deutsch, in kleinen Gruppen, zweimal pro Woche. Du übst Aussprache und Textverständnis direkt mit dem Lehrer, und jede Einheit wird aufgezeichnet, falls du einmal nicht live dabei sein kannst.',
  },
  {
    question: 'Welche Vorkenntnisse brauche ich?',
    answer:
      'Keine. Der Lernweg beginnt beim Alphabet (Level 1, Buchstabenkurs) und führt in sechs aufeinander aufbauenden Kursen bis zum selbstständigen Lesen arabischer Originaltexte. Wenn du schon lesen kannst, steigst du nach einer kurzen Einstufung direkt im passenden Level ein.',
  },
];

const QuranArabischPage = () => {
  const reasons = [
    { icon: BookOpen, h: 'Das Original statt der Übersetzung', t: 'Jede Übersetzung ist eine Interpretation. Wer Arabisch versteht, liest die Worte des Qurans so, wie sie offenbart wurden – mit allen Bedeutungsebenen.' },
    { icon: Heart, h: 'Gebet mit Verständnis', t: 'Verstehen, was du im Gebet rezitierst, verändert die Beziehung zum Gebet grundlegend – aus Routine wird bewusste Verbindung.' },
    { icon: Sparkles, h: 'Die Sprachschönheit erleben', t: 'Die sprachliche Präzision und Rhythmik des Qurans lässt sich in keiner Übersetzung abbilden. Sie erschließt sich nur im Original.' },
    { icon: GraduationCap, h: 'Zugang zum islamischen Wissen', t: 'Tafsir, Hadith und die klassischen Werke der Gelehrten sind auf Arabisch verfasst. Arabisch ist der Schlüssel zum eigenständigen Studium.' },
  ];

  const path = [
    { level: 'Level 1 · Buchstabenkurs', d: 'Arabisch lesen und schreiben lernen – inklusive grundlegender Tajweed-Regeln für die Rezitation.', link: '/arabisch-alphabet-lernen', linkText: 'Mehr zum Alphabet' },
    { level: 'Level 2–3 · Grundlagen & Aufbau', d: 'Grammatik und Wortschatz nach den bewährten Madina-Büchern – dem Standard für das Studium des klassischen Arabisch.' },
    { level: 'Level 4–5 · Fortgeschritten & Experte', d: 'Komplexe Grammatik nach Al-Ajrumiyyah – dem klassischen Grammatikwerk, mit dem seit Jahrhunderten Quran-Arabisch studiert wird.' },
    { level: 'Level 6 · Meisterstufe', d: 'Selbstständiges Lesen und Verstehen arabischer Originaltexte – vom Quran bis zur klassischen Literatur.' },
  ];

  return (
    <>
      <SEO
        title="Quran-Arabisch lernen: Den Quran im Original verstehen"
        description="Quran-Arabisch lernen mit System: Der bewährte Lernweg vom Alphabet bis zum Quran-Verständnis – Live-Unterricht online, auf Deutsch, mit klassischen Lehrwerken. Ohne Vorkenntnisse starten."
        keywords="Quran Arabisch lernen, Koran Arabisch lernen, Quran verstehen lernen, Arabisch lernen Quran, Koran verstehen ohne Übersetzung, Quran lesen lernen, Tajweed lernen, klassisches Arabisch lernen, Arabisch für den Quran"
        url="/quran-arabisch-lernen"
        faqItems={quranFaqs}
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute inset-0 opacity-50"
            style={{
              backgroundImage:
                'linear-gradient(to right,rgba(26,6,6,.06) 1px,transparent 1px),linear-gradient(to bottom,rgba(26,6,6,.06) 1px,transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/50 to-white" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <Eyebrow>Quran-Arabisch</Eyebrow>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-[-0.04em] leading-[1.05] text-[#1a0606] mb-6">
              Quran-Arabisch lernen:<br />
              den Quran <em className="not-italic oblique text-[#970000]">im Original</em> verstehen.
            </h1>
            <p className="text-[16px] md:text-[18px] leading-relaxed text-gray-500 mb-8 max-w-2xl">
              Ohne Übersetzung lesen, was du betest und rezitierst – das ist das Ziel der meisten
              unserer Schüler. Hier erfährst du, welcher Lernweg dorthin führt und wie lange
              es realistisch dauert.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/anmelden"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-bold text-[14.5px] text-white shadow-[0_8px_20px_-8px_rgba(151,0,0,0.35)] transition-all duration-150 hover:brightness-110 hover:-translate-y-px"
                style={{ background: redGradient }}
              >
                ▶ Jetzt kostenlos anmelden
              </Link>
              <a
                href="#warum-quran-arabisch"
                className="inline-flex items-center gap-2 rounded-full border-[1.5px] border-[#1a0606]/[0.18] bg-white/70 px-6 py-3.5 font-bold text-[14.5px] text-[#1a0606] transition-all duration-150 hover:border-[#970000] hover:text-[#970000]"
              >
                Mehr erfahren →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Warum Quran im Original */}
      <section id="warum-quran-arabisch" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Warum im Original</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Warum den Quran <span className="oblique font-extrabold text-[#970000]">auf Arabisch verstehen</span>?
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
              Der Quran wurde auf Arabisch offenbart – und vieles von seiner Präzision, Tiefe und
              Schönheit geht in jeder Übersetzung verloren. Vier Gründe, warum sich der Weg lohnt:
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {reasons.map(({ icon: Icon, h, t }) => (
              <div key={h} className="rounded-2xl border border-gray-100 bg-white p-5 hover:border-[#970000]/30 transition-all duration-200">
                <div className="w-9 h-9 rounded-xl bg-[#970000]/[0.08] flex items-center justify-center text-[#970000] mb-4">
                  <Icon className="h-4 w-4" />
                </div>
                <h3 className="text-[15px] font-semibold text-gray-900 mb-1.5">{h}</h3>
                <p className="text-[13px] text-gray-500 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rezitieren vs. Verstehen — Split */}
      <section id="rezitieren-vs-verstehen" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-start">
            <div>
              <Eyebrow>Der Unterschied</Eyebrow>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.15] text-gray-900 mb-5">
                Rezitieren können ist nicht <span className="oblique font-extrabold text-[#970000]">verstehen</span>
              </h2>
              <div className="space-y-4 text-[15px] leading-[1.8] text-gray-500">
                <p>
                  Viele Muslime lernen als Kinder, den Quran zu <strong className="font-semibold text-gray-700">rezitieren</strong> –
                  also die Buchstaben und Tajweed-Regeln. Das ist wertvoll, aber es ist erst die halbe Reise:
                  Die <strong className="font-semibold text-gray-700">Bedeutung</strong> bleibt ohne Grammatik
                  und Wortschatz verschlossen.
                </p>
                <p>
                  Quran-Arabisch ist klassisches Hocharabisch (Fuṣḥā). Wer es systematisch lernt – mit
                  Grammatik (Nahw), Morphologie (Sarf) und aufbauendem Wortschatz – versteht nicht nur
                  den Quran, sondern auch Hadith-Werke, Tafsir und die klassische Literatur.
                </p>
                <p>
                  Der Einstieg ist für alle gleich: <Link to="/arabisch-alphabet-lernen" className="font-semibold text-[#970000] hover:underline">das arabische Alphabet lernen</Link>.
                  Wie der gesamte Weg aussieht, zeigt unser <Link to="/arabisch-lernen" className="font-semibold text-[#970000] hover:underline">Leitfaden zum Arabisch lernen</Link>.
                </p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl bg-[#b30000] p-6 md:p-8 text-white">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/15" />
              <div className="relative">
                <Eyebrow light>Was du bei uns lernst</Eyebrow>
                <ul className="space-y-3 text-[14px] text-white/85">
                  {[
                    'Arabisch lesen und schreiben – von null',
                    'Grundlegende Tajweed-Regeln für die Rezitation',
                    'Grammatik nach Madina-Büchern und Al-Ajrumiyyah',
                    'Wortschatz durch echte Texte statt Vokabellisten',
                    'Schritt für Schritt bis zum Originaltext',
                  ].map(item => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="h-3 w-3" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/anmelden"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13px] font-semibold text-[#970000] transition-colors duration-200 hover:bg-[#f5f0ef]"
                >
                  <Play className="h-3.5 w-3.5 fill-[#970000]" />
                  Kurs finden
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Der Lernweg zum Quran-Verständnis */}
      <section id="lernweg-quran" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Der Lernweg</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Vom Alphabet zum <span className="oblique font-extrabold text-[#970000]">Quran-Verständnis</span>
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
              Unsere sechs Kursstufen folgen den klassischen Lehrwerken, mit denen Quran-Arabisch
              seit Generationen unterrichtet wird – live, online und auf Deutsch erklärt:
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {path.map(({ level, d, link, linkText }) => (
              <div key={level} className="rounded-2xl border border-gray-100 bg-white p-5 hover:border-[#970000]/30 transition-all duration-200">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-[#970000] mb-2">{level}</p>
                <p className="text-[13.5px] text-gray-500 leading-relaxed">{d}</p>
                {link && linkText && (
                  <Link to={link} className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#970000]">
                    {linkText}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/anmelden"
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-[15px] text-white shadow-[0_8px_20px_-8px_rgba(151,0,0,0.35)] transition-all duration-150 hover:brightness-110 hover:-translate-y-px"
              style={{ background: redGradient }}
            >
              Jetzt mit Level 1 starten →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="quran-arabisch-faq" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Häufige Fragen</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Häufige Fragen zum <span className="oblique font-extrabold text-[#970000]">Quran-Arabisch lernen</span>
            </h2>
          </div>
          <div className="grid gap-4 max-w-3xl">
            {quranFaqs.map(({ question, answer }) => (
              <div key={question} className="rounded-2xl border border-gray-100 bg-white p-5">
                <h3 className="text-[15px] font-semibold text-gray-900 mb-2">{question}</h3>
                <p className="text-[13.5px] text-gray-500 leading-relaxed">{answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[22px] bg-[#b30000] px-8 py-10 md:px-12 md:py-12">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/15" />
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-xl">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-[-0.03em] text-white">
                  Verstehe, <em className="not-italic oblique">was du rezitierst</em>.
                </h2>
                <p className="mt-4 text-[15px] text-white/70 font-normal">
                  Melde dich jetzt an – kostenlos und unverbindlich. Wir finden gemeinsam den richtigen Einstieg für dich.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 shrink-0">
                <Link
                  to="/anmelden"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-[15px] font-semibold text-[#970000] transition-colors duration-200 hover:bg-[#f5f0ef]"
                >
                  <Play className="h-3.5 w-3.5 fill-[#970000]" />
                  Jetzt kostenlos anmelden
                </Link>
                <Link
                  to="/arabisch-lernen"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-6 py-3 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-white/10"
                >
                  Zum Arabisch-Leitfaden
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default QuranArabischPage;
